import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Json } from '@/lib/api';
import { fileUrl, sha256, uploadToTicket } from '@/lib/supabase';
import { useToast } from './toast';
import { Button, Modal, Select } from './ui';
import { fmtDateTime, label } from '@/lib/format';
import { autoCropReceipt } from './autocrop';

export const ATTACHMENT_KINDS = ['vendor_bill', 'receipt_photo', 'signed_voucher', 'esigned_invoice', 'signed_scan', 'bank_statement', 'firc_fira', 'tds_challan', 'agreement', 'declaration', 'contract_note', 'pan_card', 'gst_certificate', 'platform_invoice', 'signed_acknowledgement', 'other'] as const;

/** Compress an image in the browser so receipt photos stay small. Non-images pass through. */
export async function compressImage(file: File, maxEdge = 1600, quality = 0.82): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return file;
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas'); c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  return await new Promise((res) => c.toBlob((b) => res(b ?? file), 'image/jpeg', quality));
}

/** Upload bytes already prepared (compressed, cropped) for a record and register them. Returns the attachment row. */
export async function uploadBlob(ownerType: string, ownerId: string, kind: string, blob: Blob, filename: string, note?: string, bucket: 'attachments' | 'statements' = 'attachments'): Promise<Json> {
  const t = await api.uploadTicket(bucket, ownerType, ownerId, filename);
  await uploadToTicket(t.bucket, t.path, blob, blob.type || 'application/octet-stream');
  return api.registerAttachment({ owner_type: ownerType, owner_id: ownerId, kind, bucket: t.bucket, storage_path: t.path, filename, mime: blob.type || 'application/octet-stream', size_bytes: blob.size, sha256: await sha256(blob), note });
}

/** Upload one file for a record and register it. Returns the attachment row. */
export async function uploadAttachment(ownerType: string, ownerId: string, kind: string, file: File, note?: string, bucket: 'attachments' | 'statements' = 'attachments'): Promise<Json> {
  const blob = kind === 'receipt_photo' || kind === 'signed_voucher' ? await compressImage(file) : file;
  return uploadBlob(ownerType, ownerId, kind, blob, blob === file ? file.name : `${file.name.replace(/\.[^.]+$/, '')}.jpg`, note, bucket);
}

type CropAsk = { file: File; crop: Blob; original: Blob; cropUrl: string; origUrl: string; resolve: (choice: { blob: Blob; cropped: boolean } | null) => void };

/** Auto-crop at capture, with a preview: receipt photos are cropped to the bill when the crop is confident; the person can keep
 *  the original. Other files pass straight through. decide() resolves to the bytes to upload, or null when the person cancels. */
export function useCropGate() {
  const [ask, setAsk] = useState<CropAsk | null>(null);
  const decide = useCallback(async (file: File, kind: string): Promise<{ blob: Blob; cropped: boolean } | null> => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || !['receipt_photo', 'vendor_bill', 'platform_invoice', 'other', 'signed_voucher'].includes(kind)) return { blob: file, cropped: false };
    const original = await compressImage(file);
    let crop: Blob | null = null;
    try { const r = await autoCropReceipt(file); if (r.cropped) crop = r.blob; } catch { crop = null; }
    if (!crop) return { blob: original, cropped: false };
    return new Promise((resolve) => setAsk({ file, crop: crop!, original, cropUrl: URL.createObjectURL(crop!), origUrl: URL.createObjectURL(original), resolve }));
  }, []);
  const close = (choice: { blob: Blob; cropped: boolean } | null) => { if (!ask) return; URL.revokeObjectURL(ask.cropUrl); URL.revokeObjectURL(ask.origUrl); ask.resolve(choice); setAsk(null); };
  const gate: ReactNode = (
    <Modal open={!!ask} onClose={() => close(null)} title="Cropped to the bill" wide>
      {ask && (
        <div className="crop-gate">
          <div><img src={ask.cropUrl} alt="Cropped" /><Button variant="primary" onClick={() => close({ blob: ask.crop, cropped: true })}>Use this crop</Button></div>
          <div><img src={ask.origUrl} alt="Original" /><Button onClick={() => close({ blob: ask.original, cropped: false })}>Use original</Button></div>
        </div>
      )}
      <p className="hint">What you pick is the evidence that is stored; its fingerprint is computed on those bytes.</p>
    </Modal>
  );
  return { gate, decide, pending: !!ask };
}

export function AttachmentLink({ a, children }: { a: Json; children?: React.ReactNode }) {
  const open = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (a.external_url) { window.open(a.external_url, '_blank'); return; }
    window.open(await fileUrl(a.bucket ?? 'attachments', a.storage_path), '_blank');
  };
  return <a href="#" onClick={open}>{children ?? a.filename ?? a.storage_path}</a>;
}

/** List of a record's attachments with an uploader. */
export function Attachments({ ownerType, ownerId, items, kinds = ATTACHMENT_KINDS as unknown as string[], defaultKind, canEdit = true, onChange, compact }: { ownerType: string; ownerId: string; items: Json[]; kinds?: string[]; defaultKind?: string; canEdit?: boolean; onChange?: (a: Json) => void; compact?: boolean }) {
  const [kind, setKind] = useState(defaultKind ?? kinds[0]);
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const { gate, decide } = useCropGate();
  const up = useMutation({
    mutationFn: async (files: FileList | File[]) => {
      const out: Json[] = [];
      for (const f of Array.from(files)) {
        const r = await decide(f, kind);
        if (!r) continue;
        out.push(await uploadBlob(ownerType, ownerId, kind, r.blob, r.blob === f ? f.name : `${f.name.replace(/\.[^.]+$/, '')}.jpg`));
      }
      return out;
    },
    onSuccess: (rows) => { toast.show(`${rows.length} file${rows.length > 1 ? 's' : ''} attached`, 'ok'); rows.forEach((r) => onChange?.(r)); qc.invalidateQueries(); },
    onError: (e) => toast.error(e),
  });
  const del = useMutation({ mutationFn: (id: string) => api.deleteAttachment(id), onSuccess: () => qc.invalidateQueries(), onError: (e) => toast.error(e) });
  return (
    <div className="attachments">
      {items.length === 0 && !canEdit && <p className="muted">No files.</p>}
      {items.length > 0 && (
        <ul className="more-list">
          {items.map((a) => (
            <li key={a.id} className="row" style={{ justifyContent: 'space-between' }}>
              <span><span className="badge tone-muted">{label(a.kind)}</span> <AttachmentLink a={a} /> <small>{a.size_bytes ? `${Math.round(a.size_bytes / 1024)} KB · ` : ''}{a.uploaded_by} · {fmtDateTime(a.uploaded_at)}</small></span>
              {canEdit && <Button size="sm" variant="ghost" onClick={() => { if (confirm('Remove this file?')) del.mutate(a.id); }}>Remove</Button>}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <div className={`row ${compact ? '' : 'form-row'}`} style={{ marginTop: 8 }}>
          <Select value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 'auto' }}>{kinds.map((k) => <option key={k} value={k}>{label(k)}</option>)}</Select>
          <div className={`dropzone ${over ? 'over' : ''}`} style={{ flex: 1, padding: compact ? 8 : 18 }}
            onClick={() => inputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files.length) up.mutate(e.dataTransfer.files); }}>
            {up.isPending ? 'Uploading…' : 'Drop files here, or tap to choose (photos, PDFs)'}
            <input ref={inputRef} type="file" multiple hidden accept="image/*,application/pdf,.csv,.xlsx,.xls" onChange={(e) => { if (e.target.files?.length) up.mutate(e.target.files); e.target.value = ''; }} />
          </div>
        </div>
      )}
      {gate}
    </div>
  );
}
